import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { useEffect, useState, useMemo } from 'react'
import { useQuery } from 'urql'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { TWILIO_PHONE_NUMBERS_QUERY } from '../graphql'
import { SAVE_TWILIO_INTEGRATION, REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormCombo from 'app/components/form/FormCombo'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormComboMultiSelect from 'app/components/form/FormComboMultiSelect'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import type { TwilioPhoneNumber } from 'app/generated/graphql'
import type { Location, LocationJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { debug, logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  // Pool numbers are optional ("None" is a valid choice). The selection is not
  // serialized into the form — onSave reads selectedPoolOptions from state — so a
  // required schema here would reject every submit.
  poolSelection: z.array(z.object({ id: z.string() })).optional(),
  aiSelection: z.string(),
  aiTestingSelection: z.string(),
})

export const createTwilioValidator = withZod(createSchema)

type TwilioPhoneNumbersQueryResult = {
  integrationOptions: {
    twilioPhoneNumbers: TwilioPhoneNumber[]
  }
}

export function TwilioNumbersModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: {
    locationId: string
    locationName: string
    twilioNumbers: Location[`twilioNumbers`]
  }
}) {
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [selectedPoolOptions, setSelectedPoolOptions] = useState<OptionType[] | undefined>(undefined)
  const [selectedAIOption, setSelectedAIOption] = useState<OptionType | undefined>(undefined)
  const [selectedAITestingOption, setSelectedAITestingOption] = useState<OptionType | undefined>(undefined)
  const [refreshing, setRefreshing] = useState(false)
  const [confirm] = useConfirmation()

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<TwilioPhoneNumbersQueryResult>({
    query: TWILIO_PHONE_NUMBERS_QUERY,
    variables: { locationId: currentValues?.locationId },
    requestPolicy: `network-only`,
  })

  const options = useMemo<OptionType[]>(() => {
    if (!data?.integrationOptions?.twilioPhoneNumbers) return []
    return [
      { id: `none`, name: `None`, value: undefined } as OptionType,
      ...data.integrationOptions.twilioPhoneNumbers.map((n) => ({
        id: n.id,
        name: n.name,
        value: n.id,
        meta: {
          isUsed: n.isUsed,
          tooltipForDisabledOption: `This phone number is connected to another location`,
        },
      })),
    ]
  }, [data])

  // Handle errors and clear refreshing state
  useEffect(() => {
    if (error) {
      logError(`Failed to load Twilio number options:`, error)
      notifyError(`There was a problem loading options. You may need to reload the page.`)
    }
    if (!fetching) setRefreshing(false)
  }, [error, fetching])

  // Set selected options when data loads and we have current values
  useEffect(() => {
    if (data?.integrationOptions?.twilioPhoneNumbers && currentValues?.twilioNumbers?.length) {
      debug(`twilio`, `currentValues`, currentValues, data.integrationOptions.twilioPhoneNumbers)
      const poolNumbers: OptionType[] = []
      const aiNumbers: OptionType[] = []
      const aiTestingNumbers: OptionType[] = []

      ;(currentValues.twilioNumbers as { phone: string; type: string; poolId?: string }[]).forEach((phoneNumber) => {
        const option = data.integrationOptions.twilioPhoneNumbers.find((hit) => hit.id.includes(phoneNumber.phone))
        if (!option) return
        const optionFormatted: OptionType = {
          id: option.id,
          name: option.name,
          value: option.id,
          meta: {
            isUsed: option.isUsed,
            tooltipForDisabledOption: `This phone number is connected to another location`,
          },
        }
        if (phoneNumber.type === `pool` && !phoneNumber.poolId) {
          poolNumbers.push(optionFormatted)
        }
        if (phoneNumber.type === `ai`) {
          aiNumbers.push(optionFormatted)
        }
        if (phoneNumber.type === `ai-testing`) {
          aiTestingNumbers.push(optionFormatted)
        }
      })
      if (poolNumbers.length) setSelectedPoolOptions(poolNumbers)
      if (aiNumbers.length) setSelectedAIOption(aiNumbers.at(0))
      if (aiTestingNumbers.length) setSelectedAITestingOption(aiTestingNumbers.at(0))
    }
  }, [data, currentValues?.twilioNumbers])

  const refresh = () => {
    setRefreshing(true)
    reexecuteQuery({ requestPolicy: `network-only` })
  }

  const onSave = async () => {
    const locationId = currentValues?.locationId || (selectedLocation?.value as string)
    if (!locationId) return

    const poolSelection =
      selectedPoolOptions?.filter((opt) => opt.id && opt.id !== `none`).map((opt) => ({ id: opt.id })) || []
    const aiSelection = selectedAIOption?.value as string | undefined
    const aiTestingSelection = selectedAITestingOption?.value as string | undefined

    const result = await gqlOpsMutation(SAVE_TWILIO_INTEGRATION, {
      locationId,
      poolSelection,
      aiSelection,
      aiTestingSelection,
      isNew: !currentValues,
    })
    const data = result.data?.saveTwilioIntegration
    if (data?.success) {
      notifySuccess(`Twilio integration saved`)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to save integration: ${data?.error || result.error || 'Unknown error'}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  // Create options with disabled state for each dropdown to show conflicts with tooltips
  const poolOptions = useMemo(() => {
    if (!options.length) return []
    const conflictingIds = new Set([selectedAIOption?.value, selectedAITestingOption?.value].filter(Boolean))
    return options.map((option) => {
      if (conflictingIds.has(option.value)) {
        return {
          ...option,
          meta: {
            ...option.meta,
            isUsed: true,
            tooltipForDisabledOption: `This phone number is already selected in another dropdown`,
          },
        }
      }
      return option
    })
  }, [options, selectedAIOption, selectedAITestingOption])

  const aiOptions = useMemo(() => {
    if (!options.length) return []
    const conflictingIds = new Set(
      [...(selectedPoolOptions?.map((opt) => opt.value) || []), selectedAITestingOption?.value].filter(Boolean),
    )
    return options.map((option) => {
      if (conflictingIds.has(option.value)) {
        return {
          ...option,
          meta: {
            ...option.meta,
            isUsed: true,
            tooltipForDisabledOption: `This phone number is already selected in another dropdown`,
          },
        }
      }
      return option
    })
  }, [options, selectedPoolOptions, selectedAITestingOption])

  const aiTestingOptions = useMemo(() => {
    if (!options.length) return []
    const conflictingIds = new Set(
      [...(selectedPoolOptions?.map((opt) => opt.value) || []), selectedAIOption?.value].filter(Boolean),
    )
    return options.map((option) => {
      if (conflictingIds.has(option.value)) {
        return {
          ...option,
          meta: {
            ...option.meta,
            isUsed: true,
            tooltipForDisabledOption: `This phone number is already selected in another dropdown`,
          },
        }
      }
      return option
    })
  }, [options, selectedPoolOptions, selectedAIOption])

  const onRemove = () => {
    return confirm({
      title: `Remove Twilio Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `twilio`,
      }).then((result) => {
        if (result.data?.removeLocationIntegration?.success) {
          notifySuccess(`Twilio integration removed`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
        }
      }),
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createTwilioValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSave}
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={5}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={5}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.twilioNumbers,
                  tooltipForDisabledOption: `This location is already connected to Twilio`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}
        {fetching ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : error ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load phone numbers</div>
        ) : options.length <= 1 ? (
          <div className="col-span-5 pt-5 text-sm text-subtle">No phone numbers available</div>
        ) : (
          <>
            <FormComboMultiSelect
              cols={5}
              name="poolSelection"
              label="Pool Phone Numbers"
              values={selectedPoolOptions}
              options={poolOptions}
              labelClassName="text-body-sm text-strong"
              onChange={(opts) => setSelectedPoolOptions(opts)}
              newLineError={false}
              checkboxSide="left"
              avoidCollisions={false}
              showErrorIcon
            />
            <FormCombo
              cols={5}
              name="aiSelection"
              label="AI Phone Number"
              options={aiOptions}
              value={selectedAIOption}
              onChange={(option) => setSelectedAIOption(option)}
              newLineError={false}
              showErrorIcon
              showSearchInDropdown={true}
              avoidCollisions={false}
              labelClassName="text-body-sm text-strong"
            />
            <FormCombo
              cols={5}
              name="aiTestingSelection"
              label="AI Testing Phone Number"
              options={aiTestingOptions}
              value={selectedAITestingOption}
              onChange={(option) => setSelectedAITestingOption(option)}
              newLineError={false}
              showErrorIcon
              showSearchInDropdown={true}
              avoidCollisions={false}
              labelClassName="text-body-sm text-strong"
            />
          </>
        )}
        <div className="col-span-1 flex items-center justify-center pt-5">
          <ArrowPathIcon
            className={classNames(`ml-2 h-5 w-5 cursor-pointer`, refreshing && `animate-spin`)}
            onClick={refresh}
          />
        </div>
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}
            <Button
              type="submit"
              form="integration-form"
              label="Save"
              mode="primary"
              size="md"
              disabled={!selectedLocation && !currentValues?.locationId}
            />
          </div>
        </div>
      </div>
    </>
  )
}
